import { getFieldTypeLabel } from "@/lib/fieldTypeCatalog";
import type { ApiError } from "@/lib/apiClient";
import type { BuilderField } from "./fieldModel";
import {
  fieldSupportsDefectTrigger,
  getDefectTriggerGradeEffect,
  hasBrokenExplicitDefectTrigger,
  isBooleanDefectType,
  isDefectRoleField,
  isOptionDefectType,
  normalizeDefectFieldType,
  resolveDefectTrigger,
} from "./defectTrigger";

export { fieldSupportsDefectTrigger };

export interface PublishErrorObject {
  code?: string;
  field_id?: number | string;
  field_key?: string;
  label?: string | null;
  message?: string;
  reason?: string;
  source_field_key?: string;
  source_scope?: string;
  source_sort_order?: number;
  target_sort_order?: number;
  target_field_key?: string;
}

/** Fehler mit optionalem Bezug auf ein Builder-Feld (für „zum Feld springen“). */
export interface PublishIssue {
  message: string;
  fieldUid?: string;
}

export function getFieldDisplayName(field: BuilderField | null | undefined, fallbackKey = ""): string {
  const label = String(field?.label || "").trim();
  if (label) return label;
  const key = String(field?.field_key || fallbackKey || "").trim();
  return key || "Unbekanntes Feld";
}

function findFieldByError(fields: BuilderField[], entry: PublishErrorObject): BuilderField | null {
  const fieldKey = String(entry.field_key || entry.target_field_key || "").trim();
  const fieldId = String(entry.field_id ?? "").trim();
  return (
    fields.find((f) => {
      if (fieldKey && f.field_key === fieldKey) return true;
      if (fieldId && String(f.field_id ?? "") === fieldId) return true;
      return false;
    }) || null
  );
}

export function buildDefectTriggerGuidance(field: BuilderField | null, fallbackKey = ""): string {
  const displayName = getFieldDisplayName(field, fallbackKey);
  const type = normalizeDefectFieldType(field?.type);
  if (isBooleanDefectType(type)) {
    return `„${displayName}“: Im Editor unter „Mangelbewertung“ festlegen, bei welcher Antwort (Ja/Nein) ein Mangel ausgelöst wird.`;
  }
  if (isOptionDefectType(type)) {
    return `„${displayName}“: Im Editor unter „Optionen“ mindestens eine Auswahl als „Mangel“ markieren.`;
  }
  const typeLabel = getFieldTypeLabel(type);
  return `„${displayName}“ (${typeLabel}): Die Rolle „Mangel“ passt zu diesem Feldtyp nicht. Rolle auf „Information“ ändern oder ein Ja/Nein- bzw. Auswahlfeld verwenden.`;
}

function formatBehaviorRuleSourceError(entry: PublishErrorObject): string {
  if (String(entry.reason || "").trim() !== "source_not_before_target") return "";
  const sourceSortOrder = Number.parseInt(String(entry.source_sort_order ?? ""), 10);
  const targetSortOrder = Number.parseInt(String(entry.target_sort_order ?? ""), 10);
  if (!Number.isInteger(sourceSortOrder) || !Number.isInteger(targetSortOrder)) return "";

  const sourceScope = String(entry.source_scope || "").trim();
  const scopeLabel = sourceScope ? ` (${sourceScope})` : "";
  const sourceFieldKey = String(entry.source_field_key || "").trim();
  const targetFieldKey = String(entry.target_field_key || entry.field_key || "").trim();
  const sourceLabel = sourceFieldKey ? `Quelle „${sourceFieldKey}"${scopeLabel}` : `Quelle${scopeLabel}`;
  const targetLabel = targetFieldKey ? `Ziel „${targetFieldKey}"` : "Ziel";
  return `${sourceLabel}: muss im Formular vor ${targetLabel} stehen (Reihenfolge ${sourceSortOrder} < ${targetSortOrder}).`;
}

export function formatPublishErrorObject(entry: PublishErrorObject, fields: BuilderField[]): string {
  const behaviorDetail = formatBehaviorRuleSourceError(entry);
  if (behaviorDetail) return behaviorDetail;

  const code = String(entry.code || "").trim();
  const fallbackFieldKey = String(entry.field_key || "").trim();
  const field = findFieldByError(fields, entry);

  if (code === "invalid_or_missing_defect_trigger") {
    return buildDefectTriggerGuidance(field, fallbackFieldKey);
  }
  if (code === "invalid_material_link") {
    const message = String(entry.message || "").trim();
    return message
      ? `„${getFieldDisplayName(field, fallbackFieldKey)}“: ${message}`
      : `Materialverknüpfung ungültig (${getFieldDisplayName(field, fallbackFieldKey)}).`;
  }
  if (code === "invalid_defect_grade_effect") {
    return `„${getFieldDisplayName(field, fallbackFieldKey)}“: Die Note für den Mangel muss zwischen 1 und 6 liegen.`;
  }

  const message = String(entry.message || "").trim();
  if (!message) return "";
  if (message.startsWith("„")) return message;
  return `„${getFieldDisplayName(field, fallbackFieldKey)}“: ${message}`;
}

function publishErrorObjectToIssue(entry: PublishErrorObject, fields: BuilderField[]): PublishIssue | null {
  const message = formatPublishErrorObject(entry, fields);
  if (!message) return null;
  const field = findFieldByError(fields, entry);
  return field ? { message, fieldUid: field.uid } : { message };
}

export function extractValidationDetail(err: ApiError): Record<string, unknown> | null {
  if (!err.body || typeof err.body !== "object") return null;
  const detail = (err.body as { detail?: unknown }).detail;
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return null;
  return detail as Record<string, unknown>;
}

function dedupeIssues(issues: PublishIssue[]): PublishIssue[] {
  const seen = new Set<string>();
  const result: PublishIssue[] = [];
  for (const issue of issues) {
    const key = `${issue.fieldUid || ""}|${issue.message}`;
    if (!issue.message || seen.has(key)) continue;
    seen.add(key);
    result.push(issue);
  }
  return result;
}

export function formatPublishValidationIssues(detail: unknown, fields: BuilderField[]): PublishIssue[] {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return [];
  const payload = detail as {
    error_objects?: PublishErrorObject[];
    errors?: unknown[];
    message?: string;
  };

  const fromObjects = (Array.isArray(payload.error_objects) ? payload.error_objects : [])
    .map((entry) => publishErrorObjectToIssue(entry, fields))
    .filter((issue): issue is PublishIssue => issue != null);
  if (fromObjects.length) return dedupeIssues(fromObjects);

  const fromErrors = (Array.isArray(payload.errors) ? payload.errors : [])
    .map((entry): PublishIssue | null => {
      if (typeof entry === "string") {
        const message = entry.trim();
        return message ? { message } : null;
      }
      if (entry && typeof entry === "object") {
        return publishErrorObjectToIssue(entry as PublishErrorObject, fields);
      }
      return null;
    })
    .filter((issue): issue is PublishIssue => issue != null);
  if (fromErrors.length) return dedupeIssues(fromErrors);

  const summary = String(payload.message || "").trim();
  return summary ? [{ message: summary }] : [];
}

export function formatPublishValidationDetail(detail: unknown, fields: BuilderField[]): string[] {
  return formatPublishValidationIssues(detail, fields).map((issue) => issue.message);
}

export function formatPublishApiIssues(err: ApiError, fields: BuilderField[]): PublishIssue[] {
  const detail = extractValidationDetail(err);
  if (detail) {
    const formatted = formatPublishValidationIssues(detail, fields);
    if (formatted.length) return formatted;
    const summary = String(detail.message || "").trim();
    if (summary) return [{ message: summary }];
  }
  // FastAPI liefert bei einfachen 422ern detail als String.
  const body = err.body as { detail?: unknown } | undefined;
  if (typeof body?.detail === "string" && body.detail.trim()) {
    const message = body.detail.trim();
    const match = /field_key=([A-Za-z0-9._:-]+)/.exec(message);
    const field = match ? fields.find((f) => f.field_key === match[1]) : null;
    return [field ? { message, fieldUid: field.uid } : { message }];
  }
  return [{ message: err.message || "Publizieren fehlgeschlagen." }];
}

export function formatPublishApiError(err: ApiError, fields: BuilderField[]): string[] {
  return formatPublishApiIssues(err, fields).map((issue) => issue.message);
}

/**
 * Lokale Prüfung der Mangel-Regeln (spiegelt die Publish-Validierung des Backends).
 * Gibt pro Feld höchstens einen Eintrag zurück.
 */
export function collectDefectTriggerIssues(fields: BuilderField[]): PublishIssue[] {
  const issues: PublishIssue[] = [];

  fields.forEach((field) => {
    if (!isDefectRoleField(field)) return;
    const displayName = getFieldDisplayName(field);

    if (!fieldSupportsDefectTrigger(field.type)) {
      issues.push({ fieldUid: field.uid, message: buildDefectTriggerGuidance(field) });
      return;
    }

    const options =
      field.options_override_json && typeof field.options_override_json === "object" && !Array.isArray(field.options_override_json)
        ? field.options_override_json
        : {};

    const resolved = resolveDefectTrigger(field);
    if (!resolved) {
      if (hasBrokenExplicitDefectTrigger(options)) {
        issues.push({
          fieldUid: field.uid,
          message: `„${displayName}“: Die gespeicherte Mangel-Regel ist unvollständig. Im Editor die Mangelbewertung einmal bestätigen („Übernehmen“) und speichern.`,
        });
        return;
      }
      issues.push({ fieldUid: field.uid, message: buildDefectTriggerGuidance(field) });
      return;
    }

    const rawGrade = resolved.trigger.grade_effect;
    if (rawGrade !== undefined) {
      const grade = getDefectTriggerGradeEffect(resolved.trigger);
      if (grade == null || grade < 1 || grade > 6) {
        issues.push({
          fieldUid: field.uid,
          message: `„${displayName}“: Die Note für den Mangel muss zwischen 1 und 6 liegen.`,
        });
      }
    }
  });

  return dedupeIssues(issues);
}

export function validateDefectTriggersBeforePublish(fields: BuilderField[]): string[] {
  return collectDefectTriggerIssues(fields).map((issue) => issue.message);
}
